/**
 * Hero: brand intro, search, category shortcuts.
 * Full width; modern gradient/overlay. Tailwind only.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/** Lead with content-rich sections for crawlers and first-time visitors. */
const CATEGORIES = [
  { id: 'explore', label: 'Explore Pakistan', path: '/points', icon: '📍' },
  { id: 'islam', label: 'Islam', path: '/islam', icon: '🕌' },
  { id: 'societies', label: 'Societies', path: '/societies', icon: '🏘️' },
  { id: 'businesses', label: 'Businesses', path: '/businesses', icon: '🏢' },
  { id: 'events', label: 'Events', path: '/events', icon: '📅' },
  { id: 'properties', label: 'Properties', path: '/properties', icon: '🏠' },
];

const HomeHeroSection = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/businesses?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/businesses');
    }
  };

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden" aria-label="Hero">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 via-white to-gray-50/80" aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-100/50 via-transparent to-transparent" aria-hidden="true" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
          Connect with Pakistan
        </h1>
        <p className="mt-4 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
          PakPoints is a community platform for residential societies, local businesses, travel places across
          Pakistan, and practical Islamic articles for everyday life.
        </p>
        <p className="mt-3 text-base text-gray-600 max-w-2xl mx-auto">
          Explore detailed place guides, read original Islam resources, and find societies and businesses near
          you—built for residents, travelers, and families.
        </p>
        <p className="mt-4 text-sm text-gray-500 max-w-2xl mx-auto">
          Start with{' '}
          <Link to="/points" className="text-emerald-700 font-medium hover:underline">
            Explore Pakistan
          </Link>
          {' '}or{' '}
          <Link to="/islam/articles" className="text-emerald-700 font-medium hover:underline">
            Islamic articles
          </Link>
          .
        </p>

        <form onSubmit={handleSearch} className="mt-8 sm:mt-10 max-w-2xl mx-auto" role="search" aria-label="Search">
          <label htmlFor="hero-search" className="sr-only">Search societies, businesses, events, places</label>
          <div className="flex rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500">
            <input
              id="hero-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search societies, businesses, events, places…"
              className="flex-1 min-w-0 px-5 py-4 text-gray-900 placeholder-gray-500 focus:outline-none"
              autoComplete="off"
            />
            <button type="submit" className="px-5 py-4 bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors shrink-0" aria-label="Search">
              Search
            </button>
          </div>
        </form>

        <div className="mt-8 flex flex-wrap justify-center gap-3 sm:gap-4">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => navigate(cat.path)}
              className="inline-flex flex-col items-center justify-center min-w-[100px] sm:min-w-[110px] py-3 px-4 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-emerald-200 transition-all text-gray-700 hover:text-emerald-700"
            >
              <span className="text-2xl mb-1" aria-hidden="true">{cat.icon}</span>
              <span className="text-sm font-medium">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeHeroSection;
